import {
    CommandType,
    handleMessage as nodeHandleMessage,
    buildDomains,
    InternalType,
    MessageType,
    GatewayNode,
    NodeMessage,
    NodeOptions,
    RequestType,
    SendMessageAction
} from '../node.js';
import {HandleReturnType, Domain} from '../domain.js';
import {emptyState} from '../state/core.js';
import {failure} from '../constants.js';
import {nodeId} from '../common/ids.js';
import {getLogger} from '../logger.js';
import {error} from '../common/messages.js';
import {exceptionToReason} from '../reason.js';
import {PeerId, Source} from '../state/types/common.js';
import {friendlily} from '../common/utilities.js';
import {MessageReceiver} from '../common/channel.js';
import {State} from '../state/types/state.js';
import {IOGateway} from '../../gateway';

type LocalNodeOptions = NodeOptions

export class LocalNode implements GatewayNode {
    constructor(private readonly handler: MessageReceiver<NodeMessage<CommandType | RequestType>>) {
    }

    close(): void {
        // do nothing
    }

    message(msg: NodeMessage<RequestType>) {
        this.handler(msg);
    }

    addSource(source: Source): void {
        // do nothing
    }

    removeSource(source: Source): void {
        const msg: NodeMessage<CommandType> = {
            origin: 'local',
            source,
            body: {type: 'commands/source-removed'}
        }
        return this.handler(msg);
    }
}

const log = getLogger('gateway.node.local');

function handleMessage(state: State, msg: NodeMessage<RequestType | InternalType | CommandType>): HandleReturnType {
    const {source, body, origin} = msg;
    // const nodeId = state.ids.nodeId;
    try {
        if (body['dump']) {
            log.info(`state dump:\n${JSON.stringify(friendlily(state), null, '\t')}`);
            return [state, []];
        } else if (origin === 'cluster') {
            return [state, []];
        } else {
            return nodeHandleMessage(state, msg, source);
        }
    } catch (e) {
        log.error(`Error handling message ${JSON.stringify(msg)}`, e);
        const m = body as {
            request_id?: string
            peer_id?: PeerId
        };
        return [state, [error(undefined, source, m.request_id, m.peer_id, exceptionToReason(e, failure))]];
    }

}

function hasReceiver(address): address is { receive: MessageReceiver<IOGateway.Message<unknown>> } {
    return 'receive' in address && address.receive;
}

function processResponse<T>(response: SendMessageAction<T>) {
    switch (response.receiver.type) {
        case 'cluster':
            return;
        case 'node':
            return;
        case 'peer':
            return;
        case 'local': {
            const {receiver, body} = response;
            if (log.enabledFor('debug')) {
                log.debug(`Sending message ${JSON.stringify(body, null, '\t')} to ${JSON.stringify(receiver)}`);
            }
            if (hasReceiver(receiver)) {
                receiver.receive(body);
            }
            break;
        }
        default: {
            log.error(`Unable to process response ${JSON.stringify(response)}`);
            break;
        }
    }
}

function processMessage(state: State, msg: NodeMessage<CommandType | InternalType | RequestType>) {
    try {
        if (log.enabledFor('trace')) {
            log.trace(`domain handler processing message ${JSON.stringify(msg)}`);
        }
        // todo event logger
        const [newState, responses] = handleMessage(state, msg);
        if (responses) {
            for (const response of responses) {
                processResponse(response);
            }
        }
        return newState ?? state;
    } catch (e) {
        log.error(`error handling message ${JSON.stringify(msg)}`, e);
        return state;
    }

}

export function localNode<T extends MessageType = MessageType>(domains: Domain<T>[], options?: LocalNodeOptions): GatewayNode {
    let state: State;
    // const ser = new AsyncSerializer()
    const handler = (msg: NodeMessage<CommandType | InternalType | RequestType>) => {
        try {
            // state = await ser.put(() => {
                // todo handle close and destroy domains
                state = processMessage(state, msg);
                // return processMessage(state, msg);
            // });
        } catch (e) {
            log.error(`Error processing internal message ${JSON.stringify(msg)}`, e);
            throw e;
        }
    };

    const registeredDomains = buildDomains(domains);

    state = domains.reduce(
        (s, domain) => {
            return domain.init(s);
        },
        {
            ...emptyState(options?.nodeId ?? nodeId(), options?.signingKey),
            registeredDomains: registeredDomains,
            handler: handler
        } as State);
    return new LocalNode(handler);
}
